import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Target, Award, BookOpen, CheckCircle, QrCode, ExternalLink, User, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PublicProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) return;
    axios.get(`${API}/users/${slug}`)
      .then(r => setProfile(r.data))
      .catch(err => {
        if (err.response?.status === 404) setError('Profil introuvable ou privé');
        else setError('Erreur lors du chargement du profil');
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f0e17] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#20c997] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0f0e17] flex flex-col items-center justify-center gap-4 px-4">
        <div className="w-16 h-16 rounded-2xl bg-[#ff6b6b]/10 flex items-center justify-center">
          <AlertCircle size={28} className="text-[#ff6b6b]" />
        </div>
        <h1 className="text-white text-xl font-bold text-center">{error}</h1>
        <p className="text-[#64748b] text-sm text-center">Ce profil est peut-être privé ou n'existe pas.</p>
        <button
          onClick={() => navigate('/')}
          className="mt-2 px-5 py-2.5 bg-[#20c997] text-[#0f0e17] rounded-xl text-sm font-bold hover:opacity-90 transition-opacity"
        >
          Retour à l'accueil
        </button>
      </div>
    );
  }

  const competencies = profile?.competencies || [];
  const certifications = profile?.certifications || [];

  return (
    <div className="min-h-screen bg-[#0f0e17]">
      {/* Barre de navigation Kurso */}
      <header className="border-b border-[#2a2935] px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate('/')} className="text-[#20c997] font-heading font-bold text-lg">
          Kurso
        </button>
        <span className="text-[#64748b] text-xs">Profil public vérifié</span>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">

        {/* Carte identité */}
        <div className="bg-[#16161e] border border-[#2a2935] rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#ff6b6b]/20 to-[#20c997]/20 border border-white/10 flex items-center justify-center shrink-0">
              <User size={28} className="text-white/60" />
            </div>
            <div className="flex-1">
              <h1 className="text-white text-2xl font-bold font-heading">{profile?.name}</h1>
              {profile?.title && <p className="text-[#ff6b6b] text-sm mt-1">{profile.title}</p>}
              <div className="flex items-center gap-2 mt-2">
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#20c997]/10 border border-[#20c997]/20 text-[#20c997] font-medium">
                  Profil Kurso vérifié
                </span>
                {certifications.length > 0 && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#ffc107]/10 border border-[#ffc107]/20 text-[#ffc107] font-medium">
                    {certifications.length} certification{certifications.length > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Compétences Kurso */}
        {competencies.length > 0 && (
          <div>
            <h2 className="text-white font-semibold text-base mb-3 flex items-center gap-2">
              <Target size={16} className="text-[#ff6b6b]" />
              Compétences validées Kurso
            </h2>
            <div className="space-y-3">
              {competencies.map((comp, i) => {
                const pct = comp.total > 0 ? Math.round((comp.completed / comp.total) * 100) : 0;
                return (
                  <div key={i} className="bg-[#16161e] border border-[#2a2935] rounded-xl p-4" data-testid={`public-comp-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white text-sm font-semibold">{comp.name}</span>
                      <span className="text-[#64748b] text-xs">{pct}%</span>
                    </div>
                    <div className="w-full h-1.5 bg-[#2a2935] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#20c997] rounded-full transition-all duration-700"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <p className="text-[#64748b] text-xs mt-1.5">
                      {comp.completed}/{comp.total} cours complété{comp.completed > 1 ? 's' : ''}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Certifications */}
        {certifications.length > 0 && (
          <div>
            <h2 className="text-white font-semibold text-base mb-3 flex items-center gap-2">
              <Award size={16} className="text-[#ffc107]" />
              Certifications
            </h2>
            <div className="space-y-3">
              {certifications.map((cert, i) => {
                const dateStr = cert.completed_at
                  ? new Date(cert.completed_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })
                  : '';
                const verifyUrl = `${window.location.origin}/certifications/verify/${cert.verification_hash}`;
                return (
                  <div key={i} className="bg-[#16161e] border border-[#2a2935] rounded-xl p-4 flex items-start gap-3" data-testid={`public-cert-${i}`}>
                    <div className="w-9 h-9 rounded-xl bg-[#ffc107]/10 flex items-center justify-center shrink-0">
                      <Award size={16} className="text-[#ffc107]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium">{cert.course_title}</p>
                      {dateStr && <p className="text-[#64748b] text-xs mt-0.5">Obtenu le {dateStr}</p>}
                    </div>
                    <a
                      href={verifyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-[#20c997] hover:opacity-80 transition-opacity shrink-0"
                      data-testid={`verify-cert-${i}`}
                    >
                      <CheckCircle size={12} /> Vérifier
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Cours complétés (si activé) */}
        {profile?.completed_courses?.length > 0 && (
          <div>
            <h2 className="text-white font-semibold text-base mb-3 flex items-center gap-2">
              <BookOpen size={16} className="text-[#3b82f6]" />
              Formations complétées
            </h2>
            <div className="flex flex-wrap gap-2">
              {profile.completed_courses.map((c, i) => (
                <span key={i} className="text-xs px-3 py-1.5 rounded-full bg-[#3b82f6]/10 border border-[#3b82f6]/20 text-[#3b82f6]">
                  {c.title}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="border-t border-[#2a2935] pt-6 text-center">
          <p className="text-[#64748b] text-xs">
            Compétences vérifiables sur{' '}
            <a href="https://mykurso.fr" className="text-[#20c997] hover:underline">kurso.app</a>
          </p>
          <p className="text-[#2a2935] text-[10px] mt-1">Plateforme Kurso LMS</p>
        </div>
      </div>
    </div>
  );
}
